import BpmnModdle from 'bpmn-moddle';

// Map BPMN element types to epx-flow node types
const TYPE_MAP = {
  'bpmn:StartEvent': 'epx:StartEvent',
  'bpmn:EndEvent': 'epx:EndEvent',
  'bpmn:UserTask': 'epx:UserTask',
  'bpmn:ServiceTask': 'epx:ServiceTask',
  'bpmn:ScriptTask': 'epx:ScriptTask',
  'bpmn:ManualTask': 'epx:ManualTask',
  'bpmn:SendTask': 'epx:SendTask',
  'bpmn:ReceiveTask': 'epx:ReceiveTask',
  'bpmn:BusinessRuleTask': 'epx:BusinessRuleTask',
  'bpmn:CallActivity': 'epx:CallActivity',
  'bpmn:Task': 'epx:UserTask',
  'bpmn:ExclusiveGateway': 'epx:ExclusiveGateway',
  'bpmn:ParallelGateway': 'epx:ParallelGateway',
  'bpmn:InclusiveGateway': 'epx:InclusiveGateway',
  'bpmn:ComplexGateway': 'epx:ComplexGateway',
  'bpmn:EventBasedGateway': 'epx:EventBasedGateway',
  'bpmn:IntermediateCatchEvent': 'epx:IntermediateEvent',
  'bpmn:IntermediateThrowEvent': 'epx:IntermediateThrowEvent',
  'bpmn:BoundaryEvent': 'epx:BoundaryEvent',
  'bpmn:SubProcess': 'epx:SubProcess',
  'bpmn:Transaction': 'epx:Transaction',
  'bpmn:DataObjectReference': 'epx:DataObjectReference',
  'bpmn:DataStoreReference': 'epx:DataStoreReference',
  'bpmn:TextAnnotation': 'epx:TextAnnotation',
};

const DEFAULT_NODE_WIDTH = 120;
const DEFAULT_NODE_HEIGHT = 60;
const CIRCLE_RADIUS = 36; // diameter
const DIAMOND_SIZE = 60;

export class BpmnReader {
  constructor() {
    this._moddleExtensions = {};
    this._readerPlugins = [];
    this._moddle = null; // lazily built
  }

  /**
   * Register a moddle extension descriptor (e.g. a custom namespace).
   * Must be called before read().
   * @param {string} name - Extension name (e.g. 'sf', 'custom')
   * @param {object} descriptor - JSON moddle descriptor
   */
  addModdleExtension(name, descriptor) {
    this._moddleExtensions[name] = descriptor;
    this._moddle = null; // force rebuild
  }

  /**
   * Register a reader plugin that can extract custom properties from parsed elements.
   * Plugin signature: { extractProperties(el, props) → void }
   * Called for each flow node / sequence flow after built-in extraction.
   * @param {object} plugin
   */
  addReaderPlugin(plugin) {
    this._readerPlugins.push(plugin);
  }

  _getModdle() {
    if (!this._moddle) {
      this._moddle = new BpmnModdle(this._moddleExtensions);
    }
    return this._moddle;
  }

  async read(xmlString) {
    const moddle = this._getModdle();
    const { rootElement: definitions } = await moddle.fromXML(xmlString);

    const nodes = [];
    const edges = [];

    // Process each root element (Process, Collaboration).
    // Track which processes are already read via Collaboration to avoid duplicates.
    const processesReadViaCollaboration = new Set();

    // Collect collaboration-level metadata for round-trip (id, sf:guid/code)
    const collaborations = [];

    // First pass: read Collaborations (which internally read their referenced Processes)
    for (const rootEl of definitions.rootElements || []) {
      if (rootEl.$type === 'bpmn:Collaboration') {
        // Extract collaboration-level sf: properties (guid, code, etc.)
        const collabProps = {};
        for (const plugin of this._readerPlugins) {
          if (typeof plugin.extractProperties === 'function') {
            plugin.extractProperties(rootEl, collabProps);
          }
        }
        collaborations.push({ id: rootEl.id, props: collabProps });
        this._readCollaboration(rootEl, definitions, nodes, edges, processesReadViaCollaboration);
      }
    }

    // Collect standalone process metadata (id, sf:guid/code/version)
    const standaloneProcesses = [];

    // Second pass: read standalone Processes that were NOT already read via a Collaboration
    for (const rootEl of definitions.rootElements || []) {
      if (rootEl.$type === 'bpmn:Process' && !processesReadViaCollaboration.has(rootEl.id)) {
        const procProps = {};
        for (const plugin of this._readerPlugins) {
          if (typeof plugin.extractProcessProperties === 'function') {
            plugin.extractProcessProperties(rootEl, procProps);
          }
        }
        standaloneProcesses.push({ id: rootEl.id, props: procProps });
        this._readProcess(rootEl, definitions, nodes, edges);
      }
    }

    // Apply DI positions from BPMNDiagram
    this._applyDiagramPositions(definitions, nodes, edges);

    // Preserve original definitions metadata for round-trip fidelity
    const meta = {
      targetNamespace: definitions.targetNamespace || 'http://www.epx.com/schema/1.0',
      definitionsId: definitions.id || 'Definitions_1',
      collaborations,
      standaloneProcesses,
    };

    return { nodes, edges, meta };
  }

  _readCollaboration(collaboration, definitions, nodes, edges, processesReadViaCollaboration) {
    // Build a map of process id → bpmn:Process element
    const processMap = {};
    for (const rootEl of definitions.rootElements || []) {
      if (rootEl.$type === 'bpmn:Process') {
        processMap[rootEl.id] = rootEl;
      }
    }

    // Read each Participant as a Pool node
    for (const participant of collaboration.participants || []) {
      const poolNode = {
        id: participant.id,
        type: 'epx:Participant',
        x: 100,
        y: 100,
        properties: {
          name: participant.name || '',
          processRef: participant.processRef ? participant.processRef.id || participant.processRef : '',
        },
      };
      // Extract participant-level sf: properties (guid, code, etc.) via plugins
      for (const plugin of this._readerPlugins) {
        if (typeof plugin.extractProperties === 'function') {
          plugin.extractProperties(participant, poolNode.properties);
        }
      }
      // Do NOT set poolNode.text — Participant renders its own rotated text
      // from properties.name. Setting text here would create a LogicFlow overlay.
      nodes.push(poolNode);

      // Read the referenced process's flow elements
      const processRef = participant.processRef;
      const process = typeof processRef === 'string'
        ? processMap[processRef]
        : processRef;
      if (process) {
        // Mark this process as already read so the outer loop doesn't duplicate it
        processesReadViaCollaboration.add(process.id);

        // Extract process-level sf: properties (guid, code, version, etc.) via plugins
        // and store them on the participant node so the writer can re-apply them.
        const processProps = {};
        for (const plugin of this._readerPlugins) {
          if (typeof plugin.extractProcessProperties === 'function') {
            plugin.extractProcessProperties(process, processProps);
          }
        }
        if (Object.keys(processProps).length > 0) {
          poolNode.properties._processProps = processProps;
        }

        // Lanes must be added BEFORE flow nodes so containers render behind them in SVG z-order
        this._readLaneSets(process, nodes);
        this._readProcess(process, definitions, nodes, edges);
      }
    }

    // Read MessageFlows between pools
    for (const mf of collaboration.messageFlows || []) {
      const edge = {
        id: mf.id,
        type: 'epx:MessageFlow',
        sourceNodeId: mf.sourceRef ? (mf.sourceRef.id || mf.sourceRef) : null,
        targetNodeId: mf.targetRef ? (mf.targetRef.id || mf.targetRef) : null,
        properties: {
          name: mf.name || '',
        },
      };
      if (mf.name) {
        edge.text = mf.name;
      }
      edges.push(edge);
    }
  }

  _readLaneSets(process, nodes) {
    if (!process.laneSets || !process.laneSets.length) return;

    for (const laneSet of process.laneSets) {
      for (const lane of laneSet.lanes || []) {
        const laneNode = {
          id: lane.id,
          type: 'epx:Lane',
          x: 100,
          y: 100,
          properties: {
            name: lane.name || '',
            // Store references to flow nodes contained in this lane
            flowNodeRefs: (lane.flowNodeRef || []).map(ref => ref.id || ref),
          },
        };
        // Extract lane-level sf: properties (guid, etc.) via plugins
        for (const plugin of this._readerPlugins) {
          if (typeof plugin.extractProperties === 'function') {
            plugin.extractProperties(lane, laneNode.properties);
          }
        }
        // Do NOT set laneNode.text — Lane renders its own rotated text
        // from properties.name. Setting text here would create a LogicFlow overlay.
        nodes.push(laneNode);

        // Nested lane sets (child lanes)
        if (lane.childLaneSet) {
          for (const childLane of lane.childLaneSet.lanes || []) {
            const childNode = {
              id: childLane.id,
              type: 'epx:Lane',
              x: 100,
              y: 100,
              properties: {
                name: childLane.name || '',
                flowNodeRefs: (childLane.flowNodeRef || []).map(ref => ref.id || ref),
                parentLane: lane.id,
              },
            };
            // Extract child lane-level sf: properties via plugins
            for (const plugin of this._readerPlugins) {
              if (typeof plugin.extractProperties === 'function') {
                plugin.extractProperties(childLane, childNode.properties);
              }
            }
            // Do NOT set childNode.text — Lane renders its own rotated text from properties.name.
            nodes.push(childNode);
          }
        }
      }
    }
  }

  _readProcess(process, definitions, nodes, edges) {
    // Collect default flow IDs from gateways/activities that have a `default` attribute
    const defaultFlowIds = new Set();
    for (const el of process.flowElements || []) {
      if (el.default) {
        const defaultId = el.default.id || el.default;
        defaultFlowIds.add(defaultId);
      }
    }

    for (const el of process.flowElements || []) {
      if (el.$type === 'bpmn:SequenceFlow') {
        const edge = this._readSequenceFlow(el);
        // Mark as default flow if the source element's `default` points to this flow
        if (defaultFlowIds.has(el.id)) {
          edge.properties.isDefault = true;
        }
        edges.push(edge);
      } else if (el.$type === 'bpmn:DataObjectReference' || el.$type === 'bpmn:DataStoreReference') {
        nodes.push(this._readArtifactNode(el));
      } else if (TYPE_MAP[el.$type]) {
        nodes.push(this._readFlowNode(el));
        // Read boundary events attached to tasks
        if (el.boundaryEventRefs) {
          for (const be of el.boundaryEventRefs) {
            nodes.push(this._readFlowNode(be));
          }
        }
      }
    }

    // Read artifacts (Group, TextAnnotation) from the process
    for (const art of process.artifacts || []) {
      if (art.$type === 'bpmn:Group') {
        const node = {
          id: art.id,
          type: 'epx:Group',
          x: 100,
          y: 100,
          properties: {
            name: art.categoryValueRef ? (art.categoryValueRef.value || '') : '',
          },
        };
        nodes.push(node);
      } else if (art.$type === 'bpmn:TextAnnotation') {
        const node = {
          id: art.id,
          type: 'epx:TextAnnotation',
          x: 100,
          y: 100,
          properties: {
            text: art.text || '',
          },
        };
        // Extract sf: properties (guid, etc.) via plugins
        for (const plugin of this._readerPlugins) {
          if (typeof plugin.extractProperties === 'function') {
            plugin.extractProperties(art, node.properties);
          }
        }
        if (art.text) {
          node.text = art.text;
        }
        nodes.push(node);
      } else if (art.$type === 'bpmn:Association') {
        const edge = {
          id: art.id,
          type: 'epx:Association',
          sourceNodeId: art.sourceRef ? (art.sourceRef.id || art.sourceRef) : null,
          targetNodeId: art.targetRef ? (art.targetRef.id || art.targetRef) : null,
          properties: {
            associationDirection: art.associationDirection || 'None',
          },
        };
        // Extract sf: properties (guid, etc.) via plugins
        for (const plugin of this._readerPlugins) {
          if (typeof plugin.extractProperties === 'function') {
            plugin.extractProperties(art, edge.properties);
          }
        }
        edges.push(edge);
      }
    }

    // Read DataInputAssociation / DataOutputAssociation from flow elements
    for (const el of process.flowElements || []) {
      if (el.dataInputAssociations) {
        for (const dia of el.dataInputAssociations) {
          const sourceRef = dia.sourceRef && dia.sourceRef.length ? (dia.sourceRef[0].id || dia.sourceRef[0]) : null;
          edges.push({
            id: dia.id,
            type: 'epx:DataInputAssociation',
            sourceNodeId: sourceRef,
            targetNodeId: el.id,
            properties: {},
          });
        }
      }
      if (el.dataOutputAssociations) {
        for (const doa of el.dataOutputAssociations) {
          const targetRef = doa.targetRef ? (doa.targetRef.id || doa.targetRef) : null;
          edges.push({
            id: doa.id,
            type: 'epx:DataOutputAssociation',
            sourceNodeId: el.id,
            targetNodeId: targetRef,
            properties: {},
          });
        }
      }
    }
  }

  _readArtifactNode(el) {
    const type = TYPE_MAP[el.$type];
    const name = el.name || '';
    const node = {
      id: el.id,
      type,
      x: 100,
      y: 100,
      properties: { name },
    };
    // Extract sf: properties (guid, etc.) via plugins
    for (const plugin of this._readerPlugins) {
      if (typeof plugin.extractProperties === 'function') {
        plugin.extractProperties(el, node.properties);
      }
    }
    if (name) {
      node.text = name;
    }
    return node;
  }

  _readFlowNode(el) {
    const type = TYPE_MAP[el.$type] || 'epx:UserTask';
    const properties = this._extractProperties(el);
    const name = el.name || '';

    // Extract BPMN event definitions (timerEventDefinition, messageEventDefinition, etc.)
    this._extractEventDefinition(el, properties);

    // Read cancelActivity / isInterrupting for BoundaryEvent and StartEvent
    if (el.$type === 'bpmn:BoundaryEvent' || el.$type === 'bpmn:StartEvent') {
      if (el.cancelActivity === false) {
        properties.isInterrupting = false;
      }
    }

    // Read attachedToRef for BoundaryEvent
    if (el.$type === 'bpmn:BoundaryEvent' && el.attachedToRef) {
      properties.attachedToRef = el.attachedToRef.id || el.attachedToRef;
    }

    // Read triggeredByEvent for Event Sub-Process
    if (el.$type === 'bpmn:SubProcess' && el.triggeredByEvent) {
      properties.triggeredByEvent = true;
    }

    // Read loopCharacteristics for tasks and sub-processes
    if (el.loopCharacteristics) {
      const lc = el.loopCharacteristics;
      if (lc.$type === 'bpmn:StandardLoopCharacteristics') {
        properties.loopType = 'Loop';
      } else if (lc.$type === 'bpmn:MultiInstanceLoopCharacteristics') {
        properties.loopType = lc.isSequential ? 'SequentialMultiInstance' : 'ParallelMultiInstance';
      }
    }

    // Store name in properties so custom SVG text rendering can read it
    properties.name = name;

    const node = {
      id: el.id,
      type,
      x: 100,
      y: 100,
      properties,
    };

    // Only add text for shapes that use LogicFlow's default text overlay
    // (events, gateways). Tasks/SubProcesses render their own SVG text from properties.name.
    const SELF_RENDERED_TYPES = [
      'epx:UserTask', 'epx:ServiceTask', 'epx:ScriptTask', 'epx:ManualTask',
      'epx:SendTask', 'epx:ReceiveTask', 'epx:BusinessRuleTask', 'epx:CallActivity',
      'epx:SubProcess', 'epx:Transaction',
    ];
    if (name && !SELF_RENDERED_TYPES.includes(type)) {
      node.text = name;
    }

    return node;
  }

  _readSequenceFlow(el) {
    const properties = this._extractProperties(el);
    const name = el.name || '';

    // Read conditionExpression — always extract as plain string from FormalExpression.body
    // (empty <conditionExpression/> elements have an object with no body — must not store object)
    if (el.conditionExpression) {
      const body = el.conditionExpression.body;
      if (typeof body === 'string' && body !== '') {
        properties.conditionExpression = body;
      }
    }

    const edge = {
      id: el.id,
      type: 'epx:SequenceFlow',
      sourceNodeId: el.sourceRef ? el.sourceRef.id : null,
      targetNodeId: el.targetRef ? el.targetRef.id : null,
      properties,
    };

    // Only add text when non-empty — empty text triggers NaN in edge label positioning
    if (name) {
      edge.text = name;
    }

    return edge;
  }

  _extractProperties(el) {
    const props = {};

    // Call registered reader plugins for custom property extraction
    for (const plugin of this._readerPlugins) {
      if (typeof plugin.extractProperties === 'function') {
        plugin.extractProperties(el, props);
      }
    }

    return props;
  }

  _extractEventDefinition(el, props) {
    const evtDefs = el.eventDefinitions;
    if (!evtDefs || !evtDefs.length) return;

    const EVENT_DEF_MAP = {
      'bpmn:TimerEventDefinition': 'Timer',
      'bpmn:MessageEventDefinition': 'Message',
      'bpmn:SignalEventDefinition': 'Signal',
      'bpmn:ErrorEventDefinition': 'Error',
      'bpmn:EscalationEventDefinition': 'Escalation',
      'bpmn:ConditionalEventDefinition': 'Conditional',
      'bpmn:CompensateEventDefinition': 'Compensation',
      'bpmn:CancelEventDefinition': 'Cancel',
      'bpmn:LinkEventDefinition': 'Link',
      'bpmn:TerminateEventDefinition': 'Terminate',
    };

    // Primary trigger from first event definition
    const def = evtDefs[0];
    const trigger = EVENT_DEF_MAP[def.$type];
    if (trigger) {
      props.trigger = trigger;
      props.eventDefinitionType = def.$type;
    }

    // Store all event definitions for multi-definition events
    if (evtDefs.length > 1) {
      props.eventDefinitions = evtDefs.map(d => ({
        type: EVENT_DEF_MAP[d.$type] || d.$type,
        definitionType: d.$type,
      }));
    }

    // Extract sub-attributes from each event definition
    for (const evtDef of evtDefs) {
      this._extractEventDefAttrs(evtDef, props);
    }
  }

  _extractEventDefAttrs(def, props) {
    switch (def.$type) {
      case 'bpmn:TimerEventDefinition':
        if (def.timeDuration) props.timeDuration = def.timeDuration.body || '';
        if (def.timeDate) props.timeDate = def.timeDate.body || '';
        if (def.timeCycle) props.timeCycle = def.timeCycle.body || '';
        break;
      case 'bpmn:MessageEventDefinition':
        if (def.messageRef) props.messageRef = def.messageRef.id || def.messageRef;
        break;
      case 'bpmn:SignalEventDefinition':
        if (def.signalRef) props.signalRef = def.signalRef.id || def.signalRef;
        break;
      case 'bpmn:ErrorEventDefinition':
        if (def.errorRef) props.errorRef = def.errorRef.id || def.errorRef;
        break;
      case 'bpmn:EscalationEventDefinition':
        if (def.escalationRef) props.escalationRef = def.escalationRef.id || def.escalationRef;
        break;
      case 'bpmn:ConditionalEventDefinition':
        if (def.condition) props.condition = def.condition.body || '';
        break;
      case 'bpmn:LinkEventDefinition':
        if (def.name) props.linkName = def.name;
        break;
    }
  }

  _applyDiagramPositions(definitions, nodes, edges) {
    if (!definitions.diagrams || !definitions.diagrams.length) return;

    const nodeById = {};
    for (const node of nodes) {
      nodeById[node.id] = node;
    }

    const edgeById = {};
    for (const edge of edges) {
      edgeById[edge.id] = edge;
    }

    const diagram = definitions.diagrams[0];
    const plane = diagram.plane;
    if (!plane || !plane.planeElement) return;

    for (const shape of plane.planeElement) {
      if (shape.$type === 'bpmndi:BPMNShape') {
        const bounds = shape.bounds;
        const refId = shape.bpmnElement && shape.bpmnElement.id;
        const node = nodeById[refId];
        if (node && bounds) {
          // LogicFlow uses center coordinates
          node.x = bounds.x + bounds.width / 2;
          node.y = bounds.y + bounds.height / 2;
          // For Participant and Lane, store actual dimensions from DI
          // Set both top-level (for LogicFlow/RectResize init) and properties (for BpmnWriter)
          if (node.type === 'epx:Participant' || node.type === 'epx:Lane') {
            node.width = bounds.width;
            node.height = bounds.height;
            node.properties.width = bounds.width;
            node.properties.height = bounds.height;
          }
          // Read isExpanded for Participant
          if (node.type === 'epx:Participant' && shape.isExpanded !== undefined) {
            node.properties.isExpanded = shape.isExpanded;
          }
          // Restore label position from BPMNLabel bounds (center of the bounds)
          if (shape.label && shape.label.bounds) {
            const lb = shape.label.bounds;
            if (typeof node.text === 'string') node.text = { value: node.text };
            if (!node.text) node.text = {};
            node.text.x = lb.x + lb.width / 2;
            node.text.y = lb.y + lb.height / 2;
          }
        }
      } else if (shape.$type === 'bpmndi:BPMNEdge') {
        const refId = shape.bpmnElement && shape.bpmnElement.id;
        const edge = edgeById[refId];
        if (edge) {
          if (shape.waypoint && shape.waypoint.length) {
            edge.pointsList = shape.waypoint.map(wp => ({ x: wp.x, y: wp.y }));
          }
          // Restore edge label position from BPMNLabel bounds
          if (shape.label && shape.label.bounds) {
            const lb = shape.label.bounds;
            if (typeof edge.text === 'string') edge.text = { value: edge.text };
            if (!edge.text) edge.text = {};
            edge.text.x = lb.x + lb.width / 2;
            edge.text.y = lb.y + lb.height / 2;
          }
        }
      }
    }
  }
}
